import axios from 'axios';
import { apiDelete, apiGet, apiPost } from './client.js';

export type PinStatus = {
  hasPinSet: boolean;
  pinSetAt: string | null;
  locked: boolean;
  lockedUntil: string | null;
};

export type StaffPinStatus = PinStatus & {
  userId: string;
  firstName: string;
  lastName: string;
  role: string;
};

export function getPinStatus() {
  return apiGet<PinStatus>('/api/v1/users/pin/status').then((r) => r.data);
}

/** Manager+ — staff PIN overview (set / locked / not set) */
export function listStaffPinStatuses() {
  return apiGet<StaffPinStatus[]>('/api/v1/users/pin/statuses').then((r) => r.data);
}

export function setPin(body: { pin: string; currentPin?: string }) {
  return apiPost<{ success: boolean; pinSetAt: string | null }, { pin: string; currentPin?: string }>(
    '/api/v1/users/pin/set',
    body,
  ).then((r) => r.data);
}

export type VerifyPinResult = {
  valid: boolean;
  locked?: boolean;
  lockedUntil?: string | null;
  attemptsRemaining?: number;
};

export type ManagerOverrideAuthorizedBy = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
};

export type VerifyOverrideResult = {
  approved: boolean;
  authorizedBy: ManagerOverrideAuthorizedBy | null;
  reason?: 'no_match' | 'locked' | 'insufficient_role';
};

export async function verifyPin(body: { pin: string }): Promise<VerifyPinResult> {
  try {
    const r = await apiPost<VerifyPinResult, { pin: string }>('/api/v1/users/pin/verify', body);
    return r.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      const payload = error.response.data as {
        data?: VerifyPinResult;
        errors?: { code?: string; message?: string }[];
      };
      const data = payload.data;
      const code = payload.errors?.[0]?.code;
      if (
        data &&
        (code === 'INVALID_PIN' ||
          code === 'PIN_LOCKED' ||
          data.valid === false ||
          data.locked === true)
      ) {
        return {
          valid: false,
          locked: Boolean(data.locked),
          lockedUntil: data.lockedUntil ?? null,
          attemptsRemaining: data.attemptsRemaining,
        };
      }
    }
    throw error;
  }
}

export async function verifyManagerOverride(body: {
  pin: string;
  action: string;
  requiredRole?: 'store_manager' | 'tenant_admin';
  context?: {
    ticketId?: string;
    amount?: number;
    percent?: number;
    reason?: string;
  };
}): Promise<VerifyOverrideResult> {
  const r = await apiPost<
    VerifyOverrideResult,
    {
      pin: string;
      action: string;
      requiredRole?: 'store_manager' | 'tenant_admin';
      context?: {
        ticketId?: string;
        amount?: number;
        percent?: number;
        reason?: string;
      };
    }
  >('/api/v1/users/pin/verify-override', body);
  return r.data;
}

export function clearPin() {
  return apiDelete<{ success: boolean }>('/api/v1/users/pin').then((r) => r.data);
}

export const deletePin = clearPin;

export type ResetStaffPinResult = {
  success: boolean;
  unlocked: boolean;
  pinCleared: boolean;
  pinSet: boolean;
};

/** Manager+ — unlock lockout (keep PIN), optionally clear or set a new PIN */
export function resetStaffPin(
  userId: string,
  body: { pin?: string; clearPin?: boolean } = {},
) {
  return apiPost<ResetStaffPinResult, { pin?: string; clearPin?: boolean }>(
    `/api/v1/users/${userId}/pin/reset`,
    body,
  ).then((r) => r.data);
}
